"use client";

import React, { useState, useEffect } from "react";

export default function Cable() {
  const [pathData, setPathData] = useState("");
  const [viewBox, setViewBox] = useState("0 0 1519 7144");
  const [isSafari, setIsSafari] = useState(false);

  // Detect Safari (includes iOS Safari)
  useEffect(() => {
    const ua = navigator.userAgent.toLowerCase();
    const safari = ua.includes('safari') && !ua.includes('chrome') && !ua.includes('chromium');
    setIsSafari(safari);
  }, []);

  useEffect(() => {
    const updatePath = () => {
      const isMobile = window.innerWidth < 768; // Tailwind md breakpoint

      if (isMobile) {
        // For mobile, extend the cable with loop + extended undulating section
        const yMultiplier = 3;
        const baseHeight = 7144 * yMultiplier; // ~21432
        const extensionHeight = 14000; // Extended for more cards
        const totalHeight = baseHeight + extensionHeight; // ~35432

        // Original loop path (ends at y = 21429, x = 731)
        const loopPath = `M1337.03 1.91C1293.03 ${448 * yMultiplier} 1419.6 ${901 * yMultiplier} 1116.53 ${1221 * yMultiplier}C954.94 ${1392 * yMultiplier} 679.51 ${1445 * yMultiplier} 448.53 ${1401 * yMultiplier}C217.55 ${1357 * yMultiplier} 167.03 ${1280 * yMultiplier} 350 ${1220 * yMultiplier}C533 ${1160 * yMultiplier} 750 ${1200 * yMultiplier} 862.53 ${1350 * yMultiplier}C975 ${1500 * yMultiplier} 105.03 ${1739 * yMultiplier} 251.53 ${2189 * yMultiplier}C366.42 ${2541 * yMultiplier} 704.17 ${2753 * yMultiplier} 980.03 ${2861 * yMultiplier}C1279.03 ${2977 * yMultiplier} 1501.03 ${3201 * yMultiplier} 1498.53 ${3571 * yMultiplier}C1496.03 ${3940 * yMultiplier} -35.47 ${4548 * yMultiplier} 21.03 ${5103 * yMultiplier}C77.53 ${5657 * yMultiplier} 1299.53 ${5479 * yMultiplier} 1299.53 ${6174 * yMultiplier}C1299.53 ${6870 * yMultiplier} 745.03 ${6631 * yMultiplier} 731.03 ${7143 * yMultiplier}`;

        // Extension with smooth gentle undulations (broad sweeps)
        // Starting from end point: x=731, y=21429
        // Keeping X roughly in range 200-1200 for gentle sweeps
        // More organic lateral variation while maintaining smooth curves
        const extension = `
C717 21700 620 22000 480 22300
C340 22600 220 22950 350 23250
C480 23550 680 23800 850 24050
C1020 24300 1180 24550 1150 24850
C1120 25150 920 25450 720 25750
C520 26050 380 26350 320 26650
C260 26950 350 27200 520 27450
C690 27700 950 27950 1120 28250
C1290 28550 1200 28850 1020 29150
C840 29450 580 29750 420 30050
C260 30350 200 30650 310 30950
C420 31250 650 31500 920 31750
C1190 32000 1280 32300 1150 32600
C1020 32900 780 33200 580 33500
C380 33800 280 34100 350 34400
C420 34700 620 35000 820 35250
C1020 35500 1050 35400 1050 35400
`.trim().replace(/\n/g, ' ');

        setPathData(loopPath + extension);
        setViewBox(`0 0 1519 ${totalHeight}`);
      } else {
        // Desktop: smooth rounded loop, extended path with undulations and second loop
        // Total height ~14500 (doubled from original ~7144)
        const totalHeight = 14550;
        const desktopPath = `
M1337.03 1.91
C1293 450 1420 900 1117 1220
C955 1390 680 1460 450 1440
C250 1420 120 1360 80 1260
C40 1160 80 1060 180 1000
C320 920 520 900 700 940
C880 980 980 1080 940 1220
C900 1360 800 1500 500 1750
C200 2000 100 2300 150 2600
C200 2900 500 3100 850 3250
C1200 3400 1450 3600 1480 3950
C1510 4300 1400 4700 1100 5050
C800 5400 200 5650 100 6100
C0 6550 300 6850 700 7100
C1100 7350 1400 7600 1450 8000
C1500 8400 1200 8700 800 8850
C400 9000 200 9100 150 9300
C100 9500 150 9650 300 9700
C500 9750 750 9700 950 9600
C1150 9500 1250 9350 1200 9200
C1150 9050 1000 8950 800 8950
C600 8950 450 9050 400 9200
C350 9350 400 9550 550 9750
C700 9950 950 10150 1200 10350
C1450 10550 1500 10850 1450 11200
C1400 11550 1100 11850 700 12100
C300 12350 50 12650 50 13000
C50 13350 300 13600 600 13800
C900 14000 1150 14200 1200 14450
`
          .trim()
          .replace(/\n/g, " ");

        setPathData(desktopPath);
        setViewBox(`0 0 1519 ${totalHeight}`);
      }
    };

    // Initial update
    updatePath();

    // Update on resize
    window.addEventListener("resize", updatePath);
    return () => window.removeEventListener("resize", updatePath);
  }, []);

  return (
    <svg
      width="100%"
      height="auto"
      viewBox={viewBox}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="overflow-visible"
    >
      <defs>
        {/* IMPORTANT: id="cable" must be on the path for GSAP MotionPath to read 'd' attribute */}
        <path id="cable" d={pathData} strokeLinecap="round" />

        {!isSafari && (
          <>
            {/* Mid-Tone Mask (Wide offset = Wide band) */}
            <mask id="highlight-mask-mid">
              <use href="#cable" stroke="white" strokeWidth="39" />
              <use href="#cable" stroke="black" strokeWidth="39" transform="translate(9, 9)" />
            </mask>

            {/* Highlight Mask (Narrow offset = Narrow band) */}
            <mask id="highlight-mask-high">
              <use href="#cable" stroke="white" strokeWidth="39" />
              <use href="#cable" stroke="black" strokeWidth="39" transform="translate(4, 4)" />
            </mask>
          </>
        )}
      </defs>

      {isSafari ? (
        /* Safari: Simple solid cable (SVG masks have issues) */
        <use
          href="#cable"
          stroke="#1a1528"
          strokeWidth="40"
        />
      ) : (
        /* Chrome/Firefox: Tritone mask-based lighting effect */
        <g>
          {/* 1. Base Layer: Darkest */}
          <use
            href="#cable"
            stroke="#141023"
            strokeWidth="39"
          />

          {/* 2. Mid Layer: Intermediate Color (Wide Band) */}
          <use
            href="#cable"
            stroke="#1e1931"
            strokeWidth="39"
            mask="url(#highlight-mask-mid)"
          />

          {/* 3. Highlight Layer: Brightest Color (Narrow Band on top) */}
          <use
            href="#cable"
            stroke="#2a2240"
            strokeWidth="39"
            mask="url(#highlight-mask-high)"
            style={{ opacity: 0.8 }}
          />
        </g>
      )}
    </svg>
  );
}
